import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { General } from 'icons/untitledui';
import colours from 'theme/atoms/colours';

import Container from 'shiftNotes/styles/InstantReflex/InstantReflexCard.style';
import * as StepStyled from 'shiftNotes/styles/InstantReflex/ReflexStepCard.style';
import * as DemoStyled from 'shiftNotes/styles/InstantReflex/DemoInstantReflexCard.style';
import MissingInfoCard from 'shiftNotes/components/MissingInfoCard';

import InstantReflexLoadingCard from './InstantReflexLoadingCard';
import ReflexStepCard from './ReflexStepCard';
import ShowProcedureCard from './ShowProcedureCard';
import AskForPhotoCard from './AskForPhotoCard';
import ComposeMessageCard from './ComposeMessageCard';
import { reflexStepRevealVariants } from './_transitions';
import {
  buildDemoActionSteps,
  buildDemoQuestions,
  DEMO_REFLEX_ID,
} from './demoReflexData';

interface DemoInstantReflexCardProps {
  noteId: string;
}

type AnswerValue = string | boolean | Date | null;
type DemoPhase = 'questions' | 'loading' | 'steps';

const FAKE_LOADING_MS = 1200;

export default function DemoInstantReflexCard({
  noteId,
}: DemoInstantReflexCardProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');

  const questions = useMemo(() => buildDemoQuestions(t), [t]);
  const steps = useMemo(() => buildDemoActionSteps(t), [t]);

  const [currentStep, setCurrentStep] = useState(1);
  const [phase, setPhase] = useState<DemoPhase>('questions');
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>(() =>
    questions.reduce<Record<string, AnswerValue>>((acc, question) => {
      acc[question.id] = question.input_type === 'text' ? '' : null;
      return acc;
    }, {})
  );

  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    },
    []
  );

  const totalSteps = questions.length;
  const activeQuestion = questions[currentStep - 1];
  const activeValue = answers[activeQuestion.id];

  const handleAnswerChange = (next: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [activeQuestion.id]: next }));
  };

  // Fake submit: unlike the real AskForDetailsCard, this calls no backend endpoint.
  const handleNext = () => {
    if (currentStep >= totalSteps) {
      setPhase('loading');
      loadingTimeoutRef.current = setTimeout(
        () => setPhase('steps'),
        FAKE_LOADING_MS
      );
      return;
    }
    setCurrentStep((step) => Math.min(totalSteps, step + 1));
  };

  const handlePrev = () => {
    setCurrentStep((step) => Math.max(1, step - 1));
  };

  const sharedProps = {
    question: activeQuestion.question,
    currentStep,
    totalSteps,
    onPrev: handlePrev,
    onNext: handleNext,
    canGoPrev: currentStep > 1,
    canGoNext: true,
  };

  const renderForm = (): JSX.Element => {
    if (activeQuestion.input_type === 'yes_no') {
      return (
        <MissingInfoCard
          variant="binary"
          value={activeValue as boolean | null}
          onChange={handleAnswerChange}
          {...sharedProps}
        />
      );
    }
    if (activeQuestion.input_type === 'date') {
      return (
        <MissingInfoCard
          variant="date"
          value={(activeValue as Date | null) ?? null}
          onChange={handleAnswerChange}
          {...sharedProps}
        />
      );
    }
    return (
      <MissingInfoCard
        variant="text"
        value={(activeValue as string) ?? ''}
        onChange={handleAnswerChange}
        {...sharedProps}
      />
    );
  };

  const stepItems: { key: string; node: JSX.Element }[] = [
    {
      key: 'answer',
      node: (
        <ReflexStepCard
          label={t('demoReflex.recommendedActionLabel')}
          labelColor={colours.mint[600]}
          icon={<General.InfoCircle />}
          title={steps.answerTitle}
        >
          <StepStyled.SourcesSection>
            <StepStyled.SourcesLabel>
              {t('instantReflex.sources')}
            </StepStyled.SourcesLabel>
            <StepStyled.SourcesList>
              <StepStyled.SourceItem
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                aria-label={t('demoReflex.viewPolicy')}
              >
                <StepStyled.SourceText>
                  {t('demoReflex.viewPolicy')}
                </StepStyled.SourceText>
              </StepStyled.SourceItem>
            </StepStyled.SourcesList>
          </StepStyled.SourcesSection>
        </ReflexStepCard>
      ),
    },
    {
      key: 'followup',
      node: (
        <ShowProcedureCard
          step={steps.followUp}
          stepNumber={1}
          totalSteps={3}
          label={t('demoReflex.shiftIntelligenceLabel')}
        />
      ),
    },
    {
      key: 'photo',
      node: (
        <AskForPhotoCard
          step={steps.photo}
          stepNumber={2}
          totalSteps={3}
          noteId={noteId}
          reflexId={DEMO_REFLEX_ID}
          label={t('demoReflex.captureEvidenceLabel')}
        />
      ),
    },
    {
      key: 'escalation',
      node: (
        <ComposeMessageCard
          step={steps.escalation}
          stepNumber={3}
          totalSteps={3}
          noteId={noteId}
          reflexId={DEMO_REFLEX_ID}
          label={t('demoReflex.escalateLabel')}
        />
      ),
    },
  ];

  return (
    <DemoStyled.Wrapper>
      {phase === 'questions' && renderForm()}
      {phase === 'loading' && <InstantReflexLoadingCard />}
      {phase === 'steps' && (
        <Container>
          <AnimatePresence>
            {stepItems.map((item, index) => (
              <motion.div
                key={item.key}
                custom={index}
                variants={reflexStepRevealVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                {item.node}
              </motion.div>
            ))}
          </AnimatePresence>
        </Container>
      )}
    </DemoStyled.Wrapper>
  );
}
